import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconChevronRight } from '@sorare/core/src/atoms/icons/system/iconChevronRight.ts';
import { iconHourglass } from '@sorare/core/src/atoms/icons/system/iconHourglass.ts';
import { iconX } from '@sorare/core/src/atoms/icons/system/iconX.ts';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { Bold } from '@sorare/core/src/atoms/typography/Bold';
import { fantasy } from '@sorare/core/src/lib/glossary';
import type { HexColor } from '@sorare/core/src/style/types';

import { TargetIcon } from '#src/assets/icons/TargetIcon.tsx';
import { DumbStatus } from '#src/components/managerTeam/Status/DumbStatus/index.tsx';
import { messages } from '#src/components/managerTeam/Status/messages.ts';
import {
  ContenderRightFooterState,
  getContenderRightFooterState,
} from '#src/lib/contender.ts';

import type { RightLiveLineupFooter_so5LeaderboardContender } from './__generated__/index.graphql.ts';

type Props = {
  so5LeaderboardContender: RightLiveLineupFooter_so5LeaderboardContender;
  onClick?: () => void;
};

export const RightLiveLineupFooter = ({
  so5LeaderboardContender,
  onClick,
}: Props) => {
  const rightState = getContenderRightFooterState(so5LeaderboardContender);
  if (rightState === null) {
    return null;
  }

  const { state } = rightState;

  let startIcon: ReactNode = null;
  let endIcon: ReactNode = null;
  let content: ReactNode = null;

  switch (state) {
    case ContenderRightFooterState.DIVISION_CHANGE:
      startIcon = (
        <DumbStatus size={1.5} divisionChange={rightState.divisionChange} />
      );
      content = (
        <LabelM bold>
          <FormattedMessage {...messages[rightState.divisionChange]} />
        </LabelM>
      );
      break;
    case ContenderRightFooterState.LEADERBOARD:
      content = (
        <LabelM bold>
          <FormattedMessage {...fantasy.leaderboard} />
        </LabelM>
      );
      endIcon = <FontAwesomeIcon icon={iconChevronRight} />;
      break;
    case ContenderRightFooterState.TARGET_MISSED:
    case ContenderRightFooterState.THRESHOLD_MISSED:
      startIcon = <FontAwesomeIcon icon={iconX} color="var(--c-red-400)" />;
      content = (
        <LabelM bold>
          <FormattedMessage defaultMessage="Missed" />
        </LabelM>
      );
      break;
    case ContenderRightFooterState.TARGET_SUCCESS:
    case ContenderRightFooterState.THRESHOLD_SUCCESS:
      if (state === ContenderRightFooterState.TARGET_SUCCESS) {
        startIcon = <img src={rightState.iconUrl} alt="" height={12} />;
      } else if (state === ContenderRightFooterState.THRESHOLD_SUCCESS) {
        startIcon = (
          <TargetIcon
            color={`rgb(${rightState.color})` as HexColor}
            size={1.5}
          />
        );
      }
      content = (
        <LabelM bold>
          <FormattedMessage defaultMessage="Success" />
        </LabelM>
      );
      break;
    case ContenderRightFooterState.TARGET_IN_PROGRESS:
    case ContenderRightFooterState.THRESHOLD_IN_PROGRESS:
      startIcon = <FontAwesomeIcon icon={iconHourglass} />;
      content = (
        <LabelM bold>
          <FormattedMessage defaultMessage="In Progress" />
        </LabelM>
      );
      break;
    case ContenderRightFooterState.POINTS_TO_NEXT_THRESHOLD:
      startIcon = (
        <TargetIcon color={`rgb(${rightState.color})` as HexColor} size={1.5} />
      );
      content = (
        <LabelM>
          <FormattedMessage
            defaultMessage="<b>{points}pts</b> to <b>Target {number}</b>"
            values={{
              points: rightState.points,
              number: rightState.thresholdNumber,
              b: Bold,
            }}
          />
        </LabelM>
      );
      break;
    default:
      return null;
  }

  return (
    <Button
      asSpan={!onClick}
      size="compact"
      color="quaternary"
      startIcon={startIcon}
      endIcon={endIcon}
      onClick={onClick}
    >
      {content}
    </Button>
  );
};

RightLiveLineupFooter.fragments = {
  so5LeaderboardContender: gql`
    fragment RightLiveLineupFooter_so5LeaderboardContender on So5LeaderboardContender {
      slug
      ...getContenderRightFooterState_so5LeaderboardContender
    }
    ${getContenderRightFooterState.fragments.so5LeaderboardContender}
  ` as TypedDocumentNode<RightLiveLineupFooter_so5LeaderboardContender>,
};
